import { useEffect } from 'react';
import { useHasFeatureFlag } from '../../selectors/useHasFeatureFlag';
import { useGetConfigQuery } from '../../API/applicationConfig/ApplicationConfigAPI';

/**
 * Custom hook to dynamically update the favicon based on:
 * 1. Feature flag: pinecone.zillow-closing (determines brand: zillow vs spruce)
 * 2. Environment: configuration.imagesFolder (determines env: preview/preprod/prod/nonprod)
 * 
 * Favicon structure mirrors the logo structure:
 * ui-toolkit/images/favicons/{environment}/{brand}/favicon.ico
 */
export const useFavicon = () => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const { data: configuration } = useGetConfigQuery();

  useEffect(() => {
    const updateFavicon = async () => {
      const brand = isZillowClosingEnabled ? 'zillow' : 'spruce';
      const folder = configuration?.imagesFolder;

      if (!folder) {
        return;
      }

      try {
        // Dynamically import the favicon (Vite will handle this as an asset and return a URL)
        const faviconModule = await import(`../images/favicons/${folder}/${brand}/favicon.ico`);
        const faviconUrl = faviconModule.default;

        // Find existing favicon link element or create one
        let faviconLink = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
        
        if (!faviconLink) {
          faviconLink = document.createElement('link');
          faviconLink.rel = 'shortcut icon';
          document.head.appendChild(faviconLink);
        }

        // Update the href to point to the new favicon
        faviconLink.href = faviconUrl;
      } catch (error) {
        console.error(`Failed to load favicon for ${folder}/${brand}:`, error);
      }
    };

    if (configuration?.imagesFolder) {
      void updateFavicon();
    }
  }, [isZillowClosingEnabled, configuration?.imagesFolder]);
};

